import { Component, OnDestroy, OnInit } from "@angular/core";
import { distinctUntilChanged, map, Subscription } from "rxjs";
import { ModalHandler } from "../core/modal-handler";
import { MatDialogConfig } from "@angular/material/dialog";
import { AuthService } from "../services/auth.service";
import { PortalService } from "../services/portal-service.service";
import { ContractInviteResponse, UserDetails } from "../models/user";
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { RegisterComponent } from "../register/register.component";
import { ResetComponent } from "../reset/reset.component";
import { ThemeService } from "../services/theme.service";
import { CristieUtils } from "../core/utils.service";

@Component({
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
  standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
  loading = true;
  auth: boolean;
  maint: { start: Date; duration: number; message: string; pending: boolean } = null;
  portalService: any;
  showActiveMaint: boolean;
  showPendingMaint: boolean;
  username: any;
  password: any;
  loggingIn: boolean;
  status: string;
  inviteToken: string = null;
  invite: ContractInviteResponse = null;
  inviteRegisterOpened = false;
  private routeSubscription: Subscription;

  constructor(
    private as: AuthService,
    private mh: ModalHandler,
    private ps: PortalService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  async JWTlogin() {
    this.loggingIn = true;
    this.status = "Validating credentials";
    try {
      const token: any = await this.as.JWTlogin(this.username, this.password);

      if (token) {
        const decoded = this.as.JWTDecode(token);
        const userId = decoded.sub;
        const trial = decoded.trial;
        const activeLics = decoded.activeLicenses;

        if (!trial) {
          const user: UserDetails = await this.ps.getUser(userId);
          localStorage.setItem("name", CristieUtils.encodeBase64(user.username));
          localStorage.setItem("email", CristieUtils.encodeBase64(user.email));
        } else {
          localStorage.setItem("name", CristieUtils.encodeBase64(this.username));
          localStorage.setItem("email", CristieUtils.encodeBase64(this.username));
        }

        localStorage.setItem("trial", trial);
        localStorage.setItem("activeLics", activeLics);
        if (this.inviteToken && !trial) {
          await this.confirmAndAcceptInvite(activeLics);
          return;
        }
        await this.login(trial, activeLics);
      }
    } catch (error) {
      this.loggingIn = false;
      this.mh.throwError("Unable to login", error);
    }
  }

  async login(trial: boolean, activeLics: boolean) {
    this.loggingIn = false;
    if (!trial && activeLics) await this.router.navigate(["/licenses"], { queryParams: { tab: "view" } });
    else if (!trial && !activeLics) await this.router.navigate(["/account"]);
    else await this.router.navigate(["/downloads"]);
  }

  ngOnInit() {
    this.routeSubscription = this.route.queryParamMap.pipe(
      map((params) => params.get("invite")),
      distinctUntilChanged(),
    ).subscribe((inviteToken) => {
      this.inviteToken = inviteToken;
      this.invite = null;
      this.inviteRegisterOpened = false;
      this.initialiseLogin();
    });
  }

  ngOnDestroy() {
    this.routeSubscription?.unsubscribe();
  }

  private async initialiseLogin() {
    if (this.as.isLoggedIn()) {
      if (this.inviteToken) {
        const activeLics = JSON.parse(localStorage.getItem("activeLics") || "false");
        await this.confirmAndAcceptInvite(activeLics);
        return;
      }
      const trial = JSON.parse(localStorage.getItem("trial") || "false");
      const activeLics = JSON.parse(localStorage.getItem("activeLics") || "false");
      await this.login(trial, activeLics);
      return;
    }

    this.auth = false;
    try {
      this.maint = await this.ps.getMaintenance();
      if (this.maint == null) {
        this.showActiveMaint = false;
        this.showPendingMaint = false;
        this.loading = false;
        await this.handleInviteLink();
        return;
      }
      if (this.maint.pending) {
        this.showPendingMaint = true;
        this.maint.message +=
          ". We aim to complete this maintenance within " + this.maint.duration + " hours. Sorry for any inconvenience.";
        this.mh.alert("Portal Maintenance", this.maint.message);
      } else this.showActiveMaint = true;
    } catch (error) {
      // If API is down, show a maintenance message
      console.log(error);
      this.showActiveMaint = true;
      this.maint = {
        start: new Date(),
        duration: 1,
        pending: false,
        message: "The licensing portal is currently undergoing maintenance. Please try again later.",
      };
    }
    this.loading = false;
    await this.handleInviteLink();
  }

  submitLogin(): void {
    this.JWTlogin();
  }

  resetPassword() {
    const config = new MatDialogConfig();
    config.width = "640px";
    config.maxWidth = "640px";
    this.mh.openModal(ResetComponent, config);
  }

  register() {
    const config = new MatDialogConfig();
    config.width = "840px";
    config.maxWidth = "840px";
    if (this.inviteToken) config.data = { inviteToken: this.inviteToken, invite: this.invite };
    this.mh.openModal(RegisterComponent, config, true).subscribe((registered: boolean) => {
      if (registered && this.inviteToken) {
        this.username = this.invite?.email;
        this.router.navigate(["/login"]);
      }
    });
  }

  private async handleInviteLink() {
    if (!this.inviteToken || this.inviteRegisterOpened) return;
    const inviteToken = this.inviteToken;
    try {
      const invite = await this.ps.getContractInvite(inviteToken);
      if (inviteToken !== this.inviteToken) return;
      this.invite = invite;
      if (this.invite.existingUser) {
        this.username = this.invite.email;
        return;
      }
      this.inviteRegisterOpened = true;
      this.register();
    } catch (error) {
      this.mh.throwError("Unable to load invitation", error);
    }
  }

  private async confirmAndAcceptInvite(activeLics: boolean) {
    if (!this.invite) this.invite = await this.ps.getContractInvite(this.inviteToken);
    const accepted = await this.mh.confirm(
      `Do you want to accept invitation to contract ${this.invite.contractNo} from ${this.invitedByDisplay()}?`,
      "Accept Invitation",
      true,
      "Cancel",
      "Accept",
    );
    if (accepted) {
      await this.ps.acceptContractInvite(this.inviteToken);
      localStorage.setItem("activeLics", "true");
      await this.router.navigate(["/licenses"], { queryParams: { tab: "view" } });
      return;
    }
    await this.login(false, activeLics);
  }

  private invitedByDisplay(): string {
    if (!this.invite) return "Cristie Software";
    return this.invite.invitedByEmail || "Cristie Software";
  }

  get dark(): boolean {
    return ["midnight", "dark"].includes(ThemeService.getTheme());
  }
}
